import React, { useState, useMemo } from 'react';
import { Users, Plus, Search, Edit2, Trash2, X, Briefcase, User, Phone, Mail, MapPin, AlertCircle } from 'lucide-react';
import { ClientService } from '../services/clientService';
import { AuditService, AUDIT_ACTIONS } from '../services/auditService';

const empty = () => ({ type: 'personne', nom: '', cin: '', ice: '', telephone: '', email: '', adresse: '' });

// Annuaire des clients du cabinet + nombre de dossiers par client
const ClientsList = ({ cabinetId, clients, projects, onOpenProject }) => {
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all'); // all | personne | societe

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const projectsByClient = useMemo(() => {
    const map = {};
    const key = (t, nom, cin, ice) => `${t}|${(t === 'societe' ? ice : cin) || nom}`.toUpperCase().replace(/\s+/g, '');
    projects.forEach((p) => {
      (p.clients || [{ type: 'personne', nom: p.client, cin: p.cin }]).forEach((c) => {
        const k = key(c.type === 'societe' ? 'societe' : 'personne', c.nom, c.cin, c.ice);
        (map[k] = map[k] || []).push(p);
      });
    });
    return (c) => map[key(c.type, c.nom, c.cin, c.ice)] || [];
  }, [projects]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    const base = clients.filter((c) => typeFilter === 'all' || (c.type === 'societe' ? 'societe' : 'personne') === typeFilter);
    if (!t) return base;
    return base.filter((c) => [c.nom, c.cin, c.ice, c.telephone, c.email].some((v) => v && v.toLowerCase().includes(t)));
  }, [clients, search, typeFilter]);
  const personnes = filtered.filter((c) => c.type !== 'societe');
  const societes = filtered.filter((c) => c.type === 'societe');
  const countPersonnes = clients.filter((c) => c.type !== 'societe').length;
  const countSocietes = clients.length - countPersonnes;
  const sections = [
    { key: 'personne', label: 'Personnes physiques', Icon: User, items: personnes },
    { key: 'societe', label: 'Sociétés', Icon: Briefcase, items: societes }
  ].filter((sec) => typeFilter === 'all' || sec.key === typeFilter);

  const submit = async () => {
    setError('');
    if (!form.nom.trim()) { setError('Le nom est requis'); return; }
    const dup = ClientService.findDuplicate(clients, form, editingId);
    if (dup) { setError(`${form.type === 'societe' ? 'ICE' : 'CIN'} déjà utilisé par « ${dup.nom} »`); return; }
    const r = editingId ? await ClientService.update(editingId, { ...form, cin: form.type === 'societe' ? '' : form.cin, ice: form.type === 'societe' ? form.ice : '' }) : await ClientService.add(cabinetId, form);
    if (!r.success) { setError(r.error); return; }
    AuditService.log(editingId ? AUDIT_ACTIONS.CLIENT_UPDATE : AUDIT_ACTIONS.CLIENT_CREATE, { entityType: 'client', entityId: editingId || r.id, label: form.nom });
    setShowForm(false);
    setEditingId(null);
    setForm(empty());
  };

  const edit = (c) => { setForm({ type: c.type, nom: c.nom, cin: c.cin || '', ice: c.ice || '', telephone: c.telephone || '', email: c.email || '', adresse: c.adresse || '' }); setEditingId(c.id); setShowForm(true); };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer « ${c.nom} » de l'annuaire ? Les dossiers existants ne sont pas modifiés.`)) return;
    const r = await ClientService.remove(c.id);
    if (r.success) AuditService.log(AUDIT_ACTIONS.CLIENT_DELETE, { entityType: 'client', entityId: c.id, label: c.nom });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} className="field pl-10" placeholder="Nom, CIN, ICE, téléphone…" aria-label="Rechercher un client" />
        </div>
        <button onClick={() => { setForm(empty()); setEditingId(null); setShowForm(true); }} className="btn-primary"><Plus className="h-4 w-4" /> Nouveau client</button>
      </div>

      {showForm && (
        <div className="card p-4 sm:p-5 space-y-3 border-brand-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">{editingId ? 'Modifier le client' : 'Nouveau client'}</h3>
            <button onClick={() => setShowForm(false)} className="icon-btn" aria-label="Fermer"><X className="h-5 w-5" /></button>
          </div>
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="label">Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className="field"><option value="personne">Personne physique</option><option value="societe">Société</option></select></div>
            <div><label className="label">{form.type === 'societe' ? 'Raison sociale' : 'Nom et prénom'} *</label><input type="text" value={form.nom} onChange={(e) => set('nom', e.target.value)} className="field" /></div>
            <div><label className="label">{form.type === 'societe' ? 'ICE' : 'CIN'}</label>
              <input type="text" value={form.type === 'societe' ? form.ice : form.cin} onChange={(e) => set(form.type === 'societe' ? 'ice' : 'cin', e.target.value)} className="field" /></div>
            <div><label className="label">Téléphone</label><input type="tel" value={form.telephone} onChange={(e) => set('telephone', e.target.value)} className="field" /></div>
            <div><label className="label">Email</label><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="field" /></div>
            <div><label className="label">Adresse</label><input type="text" value={form.adresse} onChange={(e) => set('adresse', e.target.value)} className="field" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button onClick={submit} className="btn-primary">{editingId ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Users className="h-4 w-4 text-brand-700" /> Annuaire</h3>
          <span className="text-xs text-slate-500">{filtered.length} client(s)</span>
        </div>
        <div className="px-4 sm:px-5 py-2 border-b border-slate-100 flex gap-1 overflow-x-auto" role="tablist" aria-label="Type de client">
          {[['all', 'Tous', clients.length], ['personne', 'Personnes physiques', countPersonnes], ['societe', 'Sociétés', countSocietes]].map(([k, label, n]) => (
            <button
              key={k}
              role="tab"
              aria-selected={typeFilter === k}
              onClick={() => setTypeFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap min-h-[32px] transition-colors ${typeFilter === k ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {label} <span className={typeFilter === k ? 'text-brand-100' : 'text-slate-400'}>({n})</span>
            </button>
          ))}
        </div>
        {clients.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">Aucun client. Les clients des nouveaux dossiers sont ajoutés automatiquement.</p>
        ) : sections.map((sec) => (
          <div key={sec.key}>
            <div className="px-4 sm:px-5 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <sec.Icon className="h-3.5 w-3.5" /> {sec.label} <span className="font-normal normal-case">({sec.items.length})</span>
            </div>
            {sec.items.length === 0 ? (
              <p className="px-4 sm:px-5 py-4 text-sm text-slate-400 italic">Aucun{sec.key === 'societe' ? 'e société' : 'e personne physique'}.</p>
            ) : (
          <ul className="divide-y divide-slate-100">
            {sec.items.map((c) => {
              const cp = projectsByClient(c);
              const open = expanded === c.id;
              return (
                <li key={c.id} className="px-4 sm:px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`p-2 rounded-lg ${c.type === 'societe' ? 'bg-indigo-50 text-indigo-700' : 'bg-brand-50 text-brand-700'}`}>
                      {c.type === 'societe' ? <Briefcase className="h-4 w-4" /> : <User className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{c.nom} <span className="text-xs font-normal text-slate-500">{c.type === 'societe' ? (c.ice ? `ICE ${c.ice}` : '') : (c.cin ? `CIN ${c.cin}` : '')}</span></p>
                      <p className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                        {c.telephone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.telephone}</span>}
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                        {c.adresse && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.adresse}</span>}
                      </p>
                    </div>
                    <button onClick={() => setExpanded(open ? null : c.id)} className={`text-xs px-2.5 py-1.5 rounded-full min-h-[32px] ${cp.length ? 'bg-brand-50 text-brand-700 hover:bg-brand-100' : 'bg-slate-100 text-slate-500'}`}>
                      {cp.length} dossier{cp.length > 1 ? 's' : ''}
                    </button>
                    <button onClick={() => edit(c)} className="icon-btn" aria-label="Modifier"><Edit2 className="h-4 w-4" /></button>
                    <button onClick={() => remove(c)} className="icon-btn hover:text-red-600 hover:bg-red-50" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {open && cp.length > 0 && (
                    <ul className="mt-2 ml-11 space-y-1">
                      {cp.map((p) => (
                        <li key={p.id}>
                          <button onClick={() => onOpenProject?.(p)} className="text-sm text-brand-700 hover:underline text-left min-h-[32px]">{p.refGlobal} · {p.natureProjet || 'projet'} · {p.commune || '-'} · {p.statut || 'En cours'}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClientsList;
